import type {
    UserGrowthDataResponse,
    UserGrowthMonthlyData,
    UserGrowthWeeklyData,
} from '../types/dashboard-data.types';

export interface GrowthTrend {
    trend: 'growth' | 'decline';
    severity: 'positive' | 'mild' | 'moderate' | 'strong';
    color: string;
    text: string;
    monthlyGrowth: number;
    averageWeeklyGrowth: number;
}

export interface WeekOverWeekGrowth {
    week_number: number;
    growth_percentage: number;
    current_users: number;
    previous_users: number;
}

/**
 * Calculate monthly growth percentage
 * Formula: ((Current Month Users - Previous Month Users) / Previous Month Users) × 100
 */
export function calculateMonthlyGrowth(monthlyDataArray: UserGrowthMonthlyData[]): number {
    if (monthlyDataArray.length < 2) return 0;
    
    // Sort by month_number (1 = most recent, ascending order)
    const sortedMonths = [...monthlyDataArray].sort((a, b) => a.month_number - b.month_number);
    
    const currentMonth = sortedMonths[0]; // Most recent month
    const previousMonth = sortedMonths[1]; // Previous month
    
    if (previousMonth.user_count === 0) {
        return currentMonth.user_count > 0 ? 100 : 0;
    }
    
    const growth = ((currentMonth.user_count - previousMonth.user_count) / previousMonth.user_count) * 100;
    return Math.round(growth * 10) / 10; // Round to 1 decimal place
}

/**
 * Calculate average weekly growth
 * Formula: ((Final Week Users / Starting Week Users)^(1/Number of Weeks) - 1) × 100
 */
export function calculateAverageWeeklyGrowth(weeklyData: UserGrowthWeeklyData[]): number {
    if (weeklyData.length < 2) return 0;
    
    // Sort by week_number (1 = most recent, 7 = oldest)
    const sortedWeeks = [...weeklyData].sort((a, b) => a.week_number - b.week_number);
    
    const finalValue = sortedWeeks[0].cumulative_users; // Week 1 (most recent)
    const startingValue = sortedWeeks[sortedWeeks.length - 1].cumulative_users; // Week 7 (oldest)
    const numberOfWeeks = sortedWeeks.length;
    
    if (startingValue === 0) return finalValue > 0 ? 100 : 0;
    
    const averageGrowth = (Math.pow(finalValue / startingValue, 1 / numberOfWeeks) - 1) * 100;
    return Math.round(averageGrowth * 10) / 10;
}

/**
 * Calculate week-over-week growth for each week
 * Formula: ((New Week Users - Previous Week Users) / Previous Week Users) × 100
 */
export function calculateWeekOverWeekGrowth(weeklyData: UserGrowthWeeklyData[]): WeekOverWeekGrowth[] {
    const sortedWeeks = [...weeklyData].sort((a, b) => a.week_number - b.week_number);
    const growthData: WeekOverWeekGrowth[] = [];
    
    for (let i = 0; i < sortedWeeks.length - 1; i++) {
        const currentWeek = sortedWeeks[i];
        const previousWeek = sortedWeeks[i + 1];
        
        let growth = 0;
        if (previousWeek.cumulative_users > 0) {
            growth = ((currentWeek.cumulative_users - previousWeek.cumulative_users) / previousWeek.cumulative_users) * 100;
        }
        
        growthData.push({
            week_number: currentWeek.week_number,
            growth_percentage: Math.round(growth * 10) / 10,
            current_users: currentWeek.cumulative_users,
            previous_users: previousWeek.cumulative_users
        });
    }
    
    return growthData;
}

/**
 * Detect trend and severity based on monthly and weekly growth
 */
export function detectTrend(monthlyGrowth: number, avgWeeklyGrowth: number): GrowthTrend {
    const isDowntrend = monthlyGrowth < 0 || avgWeeklyGrowth < 0;
    
    if (isDowntrend) {
        let severity: 'mild' | 'moderate' | 'strong' = 'mild';
        let text = 'Mild decline trend';
        
        if (Math.abs(monthlyGrowth) > 20 || Math.abs(avgWeeklyGrowth) > 10) {
            severity = 'strong';
            text = 'Strong decline trend';
        } else if (Math.abs(monthlyGrowth) > 10 || Math.abs(avgWeeklyGrowth) > 5) {
            severity = 'moderate';
            text = 'Moderate decline trend';
        }
        
        return {
            trend: 'decline',
            severity: severity,
            color: '#EF4444', // Red
            text: text,
            monthlyGrowth,
            averageWeeklyGrowth: avgWeeklyGrowth
        };
    }
    
    return {
        trend: 'growth',
        severity: 'positive',
        color: '#10B981', // Green
        text: 'Growth trend',
        monthlyGrowth,
        averageWeeklyGrowth: avgWeeklyGrowth
    };
}

/**
 * Main dashboard calculator class
 */
export class DashboardCalculator {
    private data: UserGrowthDataResponse;
    
    constructor(userGrowthData: UserGrowthDataResponse) {
        this.data = userGrowthData;
    }
    
    getMonthlyGrowth(): number {
        return calculateMonthlyGrowth(this.data.monthly_data);
    }
    
    getAverageWeeklyGrowth(): number {
        return calculateAverageWeeklyGrowth(this.data.weekly_data);
    }
    
    getWeekOverWeekGrowth(): WeekOverWeekGrowth[] {
        return calculateWeekOverWeekGrowth(this.data.weekly_data);
    }
    
    getTrend(): GrowthTrend {
        const monthlyGrowth = this.getMonthlyGrowth();
        const avgWeeklyGrowth = this.getAverageWeeklyGrowth();
        return detectTrend(monthlyGrowth, avgWeeklyGrowth);
    }
    
    /**
     * Get chart data formatted for Recharts (weekly view)
     */
    getChartData() {
        const weekOverWeekGrowth = this.getWeekOverWeekGrowth();
        const sortedWeeks = [...this.data.weekly_data].sort((a, b) => b.week_number - a.week_number);
        
        return sortedWeeks.map((week, index) => {
            // Find the corresponding week-over-week growth data
            const growthData = weekOverWeekGrowth.find(g => g.week_number === week.week_number);
            
            let changeString = '';
            if (growthData) {
                const changePercentage = growthData.growth_percentage;
                // Format the change as a percentage string
                changeString = changePercentage >= 0 
                    ? `+${changePercentage.toFixed(1)}%` 
                    : `${changePercentage.toFixed(1)}%`;
            } else {
                // For the oldest week (no previous week to compare), show new users as baseline
                const newUsersPercentage = week.new_users_this_week > 0 ? 
                    ((week.new_users_this_week / week.cumulative_users) * 100) : 0;
                changeString = `+${newUsersPercentage.toFixed(1)}%`;
            }
            
            return {
                week: `W${week.week_number}`,
                users: week.cumulative_users,
                new: week.new_users_this_week,
                change: changeString,
                date: new Date(week.start_date)
            };
        });
    }
    
    /**
     * Get chart data formatted for Recharts (monthly view)
     */
    getMonthlyChartData() {
        const monthlyData = this.data.monthly_data;
        const sortedMonths = [...monthlyData].sort((a, b) => b.month_number - a.month_number);
        
        return sortedMonths.map((month, index) => {
            let changeString = '';
            
            if (index < sortedMonths.length - 1) {
                const previousMonth = sortedMonths[index + 1];
                const growthPercentage = previousMonth.user_count > 0
                    ? ((month.user_count - previousMonth.user_count) / previousMonth.user_count) * 100
                    : month.user_count > 0 ? 100 : 0;
                
                changeString = growthPercentage >= 0 
                    ? `+${growthPercentage.toFixed(1)}%` 
                    : `${growthPercentage.toFixed(1)}%`;
            } else {
                // For the oldest month (no previous month to compare)
                const newUsersPercentage = month.new_users_this_month > 0 ? 
                    ((month.new_users_this_month / month.cumulative_users) * 100) : 0;
                changeString = `+${newUsersPercentage.toFixed(1)}%`;
            }
            
            return {
                month: new Date(month.year, month.month - 1).toLocaleDateString('en-US', { month: 'short' }),
                users: month.cumulative_users,
                new: month.new_users_this_month,
                change: changeString,
                date: new Date(month.start_date)
            };
        });
    }
    
    /**
     * Get chart data based on time filter
     * @param timeFilter 'this_week' or 'this_month'
     */
    getChartDataByFilter(timeFilter: 'this_week' | 'this_month') {
        if (timeFilter === 'this_month') {
            return this.getMonthlyChartData();
        }
        return this.getChartData();
    }
    
    /**
     * Complete dashboard summary
     */
    getDashboardSummary() {
        const trend = this.getTrend();
        return {
            growth: {
                monthly: this.getMonthlyGrowth(),
                averageWeekly: this.getAverageWeeklyGrowth(),
                weekOverWeek: this.getWeekOverWeekGrowth(),
                trend: trend
            },
            chartData: this.getChartData()
        };
    }
}

